/**
 * Offline outbox for comprehension-quiz attempts.
 *
 * Two-tier replay strategy because the Background Synchronization API is
 * Chromium-only (no Firefox, no Safari/iPad):
 *
 *   1. Chromium  — attempts are POSTed immediately; on failure they are stored
 *      in IndexedDB and a Background Sync registration replays them when the OS
 *      reports connectivity.
 *   2. Safari/iPad/Firefox — no Background Sync, so the queue is flushed
 *      imperatively whenever the app loads or regains focus (see
 *      `flushOutbox`), and whenever connectivity returns.
 *
 * The Workbox `BackgroundSyncPlugin` in the service worker owns the queue named
 * by `ATTEMPTS_QUEUE`; this module owns the application-side IndexedDB mirror
 * that guarantees a flush even where Background Sync is unavailable.
 */

import type { AttemptResult, AttemptSubmission } from "@/lib/api";
import { attempts } from "@/lib/api";

const DB_NAME = "creative-reading";
const DB_VERSION = 1;
const STORE = "attempt-outbox";
export const ATTEMPTS_QUEUE = "attempts-queue-v1";
const SYNC_TAG = "replay-attempts";

interface OutboxRecord {
  id: string;
  submission: AttemptSubmission;
  queuedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error("indexeddb open failed"));
  });
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const req = run(transaction.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexeddb tx failed"));
  });
}

function makeId(submission: AttemptSubmission): string {
  return `${submission.childId}:${submission.questionId}:${submission.clientTimestamp}`;
}

async function persist(submission: AttemptSubmission): Promise<void> {
  const db = await openDb();
  try {
    const record: OutboxRecord = {
      id: makeId(submission),
      submission,
      queuedAt: Date.now(),
    };
    await tx(db, "readwrite", (store) => store.put(record));
  } finally {
    db.close();
  }
}

async function remove(id: string): Promise<void> {
  const db = await openDb();
  try {
    await tx(db, "readwrite", (store) => store.delete(id));
  } finally {
    db.close();
  }
}

async function readAll(): Promise<OutboxRecord[]> {
  const db = await openDb();
  try {
    return await tx(db, "readonly", (store) => store.getAll() as IDBRequest<OutboxRecord[]>);
  } finally {
    db.close();
  }
}

async function requestBackgroundSync(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) return false;
  try {
    const reg = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    };
    if (!reg.sync) return false;
    await reg.sync.register(SYNC_TAG);
    return true;
  } catch {
    return false;
  }
}

export interface SubmitOutcome {
  /** Present when the attempt was graded online. */
  result: AttemptResult | null;
  /** True when the attempt was stored offline for later replay. */
  queued: boolean;
}

/**
 * Submit an attempt, queuing it for replay if the network is unavailable.
 * Returns the graded result when online, or `queued: true` when stored offline.
 */
export async function submitAttempt(
  submission: AttemptSubmission,
): Promise<SubmitOutcome> {
  if (navigator.onLine) {
    try {
      const result = await attempts.submit(submission);
      return { result, queued: false };
    } catch {
      // fall through to offline queue
    }
  }
  await persist(submission);
  await requestBackgroundSync();
  return { result: null, queued: true };
}

/**
 * Replay every queued attempt against the server. Safe to call repeatedly;
 * records are removed only after a successful POST. Call on app load, on
 * `focus`, and on the `online` event for Safari/Firefox where Background Sync
 * does not exist.
 */
export async function flushOutbox(): Promise<number> {
  if (!navigator.onLine) return 0;
  const records = await readAll();
  let flushed = 0;
  for (const record of records) {
    try {
      await attempts.submit(record.submission);
      await remove(record.id);
      flushed += 1;
    } catch {
      // Stop on the first failure; remaining records stay queued.
      break;
    }
  }
  return flushed;
}

/** Number of attempts currently waiting to sync (for a UI badge). */
export async function pendingCount(): Promise<number> {
  const records = await readAll();
  return records.length;
}

/** Wire up automatic flushing for non-Chromium browsers. Returns a cleanup fn. */
export function registerOutboxAutoFlush(): () => void {
  const handler = () => {
    void flushOutbox();
  };
  window.addEventListener("online", handler);
  window.addEventListener("focus", handler);
  // Initial drain on startup.
  handler();
  return () => {
    window.removeEventListener("online", handler);
    window.removeEventListener("focus", handler);
  };
}
